import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import {
  Loader2, LayoutDashboard, Users, FileText, Receipt, Key, Mail, LogOut, Plus, Upload, Trash2, Copy, Download, LineChart, Bot
} from 'lucide-react';
import api from '@/lib/api';
import PositionAnalysis from '@/pages/admin/PositionAnalysis';
import AgentSeymour from '@/pages/admin/AgentSeymour';

function AdminSidebar({ activeRoute }) {
  const { user, logout } = useAuth();

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/clients', label: 'Clients', icon: Users },
    { path: '/admin/positions', label: 'Position Analysis', icon: LineChart },
    { path: '/admin/agent', label: 'Agent Seymour', icon: Bot },
    { path: '/admin/insights', label: 'Insights', icon: FileText },
    { path: '/admin/bills', label: 'Bills', icon: Receipt },
    { path: '/admin/invite-codes', label: 'Invite Codes', icon: Key },
    { path: '/admin/messages', label: 'Messages', icon: Mail },
  ];

  return (
    <aside className="w-64 bg-[var(--navy-900)] text-[var(--cream)] min-h-[calc(100vh-4rem)] flex flex-col">
      <nav className="flex-1 py-4">
        {navItems.map((item) => {
          const isActive = item.exact
            ? activeRoute === item.path
            : activeRoute.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="px-4 py-2 text-sm">
          <p className="font-medium">{user?.name}</p>
          <p className="text-[var(--cream)]/60 text-xs">Administrator</p>
        </div>
        <button onClick={logout} className="sidebar-link w-full text-left mt-2">
          <LogOut className="h-5 w-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader2 className="h-8 w-8 animate-spin mx-auto mt-12" />;
  }

  return (
    <div>
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card-newspaper">
          <p className="text-sm text-[var(--muted-foreground)]">Clients</p>
          <p className="text-3xl font-serif text-[var(--navy-800)]">{stats?.total_clients || 0}</p>
        </div>
        <div className="card-newspaper">
          <p className="text-sm text-[var(--muted-foreground)]">Insights</p>
          <p className="text-3xl font-serif text-[var(--navy-800)]">{stats?.total_insights || 0}</p>
        </div>
        <div className="card-newspaper">
          <p className="text-sm text-[var(--muted-foreground)]">Active Codes</p>
          <p className="text-3xl font-serif text-[var(--gold-500)]">{stats?.active_invite_codes || 0}</p>
        </div>
        <div className="card-newspaper">
          <p className="text-sm text-[var(--muted-foreground)]">New Messages</p>
          <p className="text-3xl font-serif text-[var(--gold-500)]">{stats?.new_contact_messages || 0}</p>
        </div>
      </div>
    </div>
  );
}

function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await api.post('/clients', newClient);
      toast.success('Client created');
      if (response.data.temporary_password) {
        toast.info(`Temp password: ${response.data.temporary_password}`, { duration: 10000 });
      }
      setShowModal(false);
      setNewClient({ name: '', email: '', password: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl text-[var(--navy-800)]">Clients</h1>
        <button onClick={() => setShowModal(true)} className="btn-big btn-big-primary py-2 px-4 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add Client
        </button>
      </div>

      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      ) : clients.length === 0 ? (
        <div className="card-newspaper text-center py-12">
          <Users className="h-12 w-12 text-[var(--muted-foreground)]/30 mx-auto mb-4" />
          <p className="text-[var(--muted-foreground)]">No clients yet.</p>
        </div>
      ) : (
        <table className="table-simple">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.client_id}>
                <td className="font-medium">{client.name}</td>
                <td>{client.email}</td>
                <td>
                  <span className={`text-sm ${client.status === 'active' ? 'text-emerald-600' : 'text-[var(--muted-foreground)]'}`}>
                    {client.status}
                  </span>
                </td>
                <td className="text-sm text-[var(--muted-foreground)]">
                  {new Date(client.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--cream)] p-6 w-full max-w-md">
            <h2 className="font-serif text-2xl text-[var(--navy-800)] mb-6">Add Client</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="input-brutalist"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="input-brutalist"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password (optional)</label>
                <input
                  type="password"
                  value={newClient.password}
                  onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                  className="input-brutalist"
                  placeholder="Leave empty to auto-generate"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="btn-big flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-big btn-big-primary flex-1" disabled={creating}>
                  {creating ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentsPage({ type, title }) {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', client_id: '', file: null });
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchData();
  }, [type]);

  const fetchData = async () => {
    try {
      const [docsRes, clientsRes] = await Promise.all([
        api.get(`/documents?type=${type}`),
        api.get('/clients'),
      ]);
      setDocuments(docsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((files) => {
    if (files.length > 0) {
      setNewDoc((prev) => ({ ...prev, file: files[0] }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newDoc.file || !newDoc.title || !newDoc.client_id) {
      toast.error('Please fill all fields');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', newDoc.title);
      formData.append('type', type);
      formData.append('client_id', newDoc.client_id);
      formData.append('file', newDoc.file);

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Document uploaded');
      setShowModal(false);
      setNewDoc({ title: '', client_id: '', file: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    setDeleting(docId);
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.client_id === clientId);
    return client ? client.name : 'Unknown';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl text-[var(--navy-800)]">{title}</h1>
        <button onClick={() => setShowModal(true)} className="btn-big btn-big-primary py-2 px-4 flex items-center gap-2">
          <Upload className="h-4 w-4" /> Upload
        </button>
      </div>

      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      ) : documents.length === 0 ? (
        <div className="card-newspaper text-center py-12">
          <FileText className="h-12 w-12 text-[var(--muted-foreground)]/30 mx-auto mb-4" />
          <p className="text-[var(--muted-foreground)]">No documents yet.</p>
        </div>
      ) : (
        <table className="table-simple">
          <thead>
            <tr>
              <th>Title</th>
              <th>Client</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.document_id}>
                <td className="font-medium">{doc.title}</td>
                <td>{getClientName(doc.client_id)}</td>
                <td className="text-sm text-[var(--muted-foreground)]">
                  {new Date(doc.created_at).toLocaleDateString()}
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(doc.document_id)}
                    className="text-red-600 hover:text-red-700"
                    disabled={deleting === doc.document_id}
                  >
                    {deleting === doc.document_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--cream)] p-6 w-full max-w-md">
            <h2 className="font-serif text-2xl text-[var(--navy-800)] mb-6">Upload Document</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={newDoc.title}
                  onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                  className="input-brutalist"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <select
                  value={newDoc.client_id}
                  onChange={(e) => setNewDoc({ ...newDoc, client_id: e.target.value })}
                  className="input-brutalist"
                  required
                >
                  <option value="">Select client...</option>
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">File (PDF)</label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${isDragActive ? 'border-[var(--gold-500)] bg-[var(--gold-500)]/5' : 'border-[var(--border)]'}`}
                >
                  <input {...getInputProps()} />
                  {newDoc.file ? (
                    <p className="text-sm">{newDoc.file.name}</p>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto mb-2 text-[var(--muted-foreground)]" />
                      <p className="text-sm text-[var(--muted-foreground)]">Drop file here or click to select</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="btn-big flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-big btn-big-primary flex-1" disabled={uploading}>
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await api.get('/invite-codes');
      setCodes(response.data);
    } catch (error) {
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await api.post('/invite-codes', {});
      toast.success(`Code created: ${response.data.code}`);
      fetchCodes();
    } catch (error) {
      toast.error('Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const handleDelete = async (inviteId) => {
    try {
      await api.delete(`/invite-codes/${inviteId}`);
      toast.success('Deleted');
      fetchCodes();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl text-[var(--navy-800)]">Invite Codes</h1>
        <button onClick={handleCreate} className="btn-big btn-big-primary py-2 px-4 flex items-center gap-2" disabled={creating}>
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Generate Code
        </button>
      </div>

      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      ) : codes.length === 0 ? (
        <div className="card-newspaper text-center py-12">
          <Key className="h-12 w-12 text-[var(--muted-foreground)]/30 mx-auto mb-4" />
          <p className="text-[var(--muted-foreground)]">No invite codes yet.</p>
        </div>
      ) : (
        <table className="table-simple">
          <thead>
            <tr>
              <th>Code</th>
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((inv) => (
              <tr key={inv.invite_id}>
                <td>
                  <span className="font-mono font-bold text-lg">{inv.code}</span>
                  <button onClick={() => copyCode(inv.code)} className="ml-2 text-[var(--muted-foreground)] hover:text-[var(--navy-800)]">
                    <Copy className="h-4 w-4" />
                  </button>
                </td>
                <td>
                  <span className={`text-sm ${inv.used ? 'text-[var(--muted-foreground)]' : 'text-emerald-600'}`}>
                    {inv.used ? 'Used' : 'Active'}
                  </span>
                </td>
                <td className="text-sm text-[var(--muted-foreground)]">
                  {new Date(inv.created_at).toLocaleDateString()}
                </td>
                <td>
                  {!inv.used && (
                    <button onClick={() => handleDelete(inv.invite_id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/contact-messages');
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-8">Contact Messages</h1>

      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      ) : messages.length === 0 ? (
        <div className="card-newspaper text-center py-12">
          <Mail className="h-12 w-12 text-[var(--muted-foreground)]/30 mx-auto mb-4" />
          <p className="text-[var(--muted-foreground)]">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.contact_id} className="card-newspaper">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium">{msg.name}</p>
                  <a href={`mailto:${msg.email}`} className="text-sm text-[var(--navy-800)] underline">{msg.email}</a>
                </div>
                <span className="text-xs text-[var(--muted-foreground)]">
                  {new Date(msg.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[var(--muted-foreground)] whitespace-pre-wrap">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPortal() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (!loading && user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/portal');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--navy-800)]" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="admin-portal-page">
      {/* Header */}
      <header className="h-16 bg-[var(--navy-800)] flex items-center justify-between px-6">
        <Link to="/" className="font-serif text-xl text-[var(--cream)]">
          Coastwise Trade
        </Link>
        <span className="text-sm text-[var(--gold-500)] font-medium">Admin</span>
      </header>

      <div className="flex">
        <AdminSidebar activeRoute={location.pathname} />
        <main className="flex-1 p-8">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="positions" element={<PositionAnalysis />} />
            <Route path="agent" element={<AgentSeymour />} />
            <Route path="insights" element={<DocumentsPage type="insight" title="Insights" />} />
            <Route path="bills" element={<DocumentsPage type="bill" title="Bills" />} />
            <Route path="invite-codes" element={<InviteCodesPage />} />
            <Route path="messages" element={<MessagesPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
